import React from 'react';
import styled from 'styled-components';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { FiArrowLeft, FiInstagram } from 'react-icons/fi';
import { AiFillHome } from 'react-icons/ai';
import {
  Header as WelcomeHeader,
  HeaderContainer,
  Logo,
  LogoSJ,
  LogoMate,
  NavLinks,
  NavLink,
  NavHomeIcon,
  NavButtons,
  SignInButton,
  PrimaryButton,
  DashboardButton,
  DashboardBadge,
  Footer,
  FooterContent,
  FooterSection,
  FooterLogo,
  FooterLogoSJ,
  FooterLogoMate,
  FooterTitle,
  FooterLink,
  FooterBottom,
  FooterLine,
  FooterText,
  FooterSocialLinks,
  FooterSocialLink,
} from '../WelcomeScreen/styles';

const Icon = ({ component: Component, ...props }: { component: any; [key: string]: any }) => {
  return <Component {...props} />;
};

const Container = styled.div`
  min-height: calc(100vh - 200px);
  background: var(--bg-primary);
  padding: 2rem;
  max-width: 1000px;
  margin: 0 auto;
  font-family: 'Poppins', sans-serif;
  color: var(--text-gray);
  line-height: 1.8;
  margin-top: 6rem;
  padding-top: 3rem;

  @media (max-width: 768px) {
    padding: 1rem;
    margin-top: 5rem;
    padding-top: 2rem;
  }
`;

const Title = styled.h1`
  margin-top: 0;
  font-size: 2.5rem;
  font-weight: 900;
  margin: 0;
  font-family: 'Poppins', sans-serif;
  letter-spacing: -0.02em;
  color: var(--text-dark);

  @media (max-width: 768px) {
    font-size: 2rem;
  }

  @media (max-width: 480px) {
    font-size: 1.75rem;
  }
`;

const LastUpdated = styled.p`
  color: var(--text-muted);
  font-size: 0.9rem;
  margin-top: 0.5rem;
  margin-bottom: 2rem;
`;

const Section = styled.section`
  margin-bottom: 3rem;
`;

const SectionTitle = styled.h2`
  font-size: 1.8rem;
  font-weight: 900;
  color: var(--accent-orange);
  margin-bottom: 1rem;
  margin-top: 2rem;
  font-family: 'Poppins', sans-serif;
  letter-spacing: -0.02em;

  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const SubSectionTitle = styled.h3`
  font-size: 1.3rem;
  font-weight: 700;
  color: var(--text-dark);
  margin-top: 1.5rem;
  margin-bottom: 0.75rem;
  font-family: 'Poppins', sans-serif;

  @media (max-width: 768px) {
    font-size: 1.15rem;
  }
`;

const Paragraph = styled.p`
  font-size: 1rem;
  line-height: 1.8;
  margin-bottom: 1rem;
  color: var(--text-gray);

  @media (max-width: 768px) {
    font-size: 0.95rem;
  }
`;

const List = styled.ul`
  margin: 1rem 0;
  padding-left: 2rem;
`;

const ListItem = styled.li`
  margin-bottom: 0.75rem;
  line-height: 1.8;
  color: var(--text-gray);
`;

const HighlightBox = styled.div`
  background: rgba(255, 150, 0, 0.1);
  border-left: 4px solid var(--accent-orange);
  padding: 1.5rem;
  border-radius: 8px;
  margin: 2rem 0;
`;

const HighlightText = styled.p`
  font-size: 1rem;
  line-height: 1.8;
  color: var(--text-gray);
  margin: 0;
  font-weight: 600;
`;

const ContactInfo = styled.div`
  margin-top: 3rem;
  padding-top: 2rem;
  border-top: 2px solid var(--card-border);
`;

const TermsOfService: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  const handleSignIn = () => {
    navigate('/signin');
  };

  const handleSignUp = () => {
    navigate('/signup');
  };

  const handleDashboard = () => {
    if (user?.role === 'coach') {
      navigate('/coach/home');
    } else if (user?.role === 'athlete') {
      navigate('/jumper/home');
    }
  };

  const handleLogoClick = () => {
    navigate('/');
  };

  const scrollToHome = () => {
    navigate('/');
  };

  const scrollToFeatures = () => {
    if (location.pathname !== '/') {
      navigate('/#features');
    } else {
      navigate('/#features');
    }
  };

  const scrollToFAQ = () => {
    if (location.pathname !== '/') {
      navigate('/#faq');
    } else {
      navigate('/#faq');
    }
  };

  const scrollToPricing = () => {
    navigate('/pricing');
  };

  return (
    <>
      <WelcomeHeader>
        <HeaderContainer>
          <Logo onClick={handleLogoClick}>
            <LogoSJ>sj</LogoSJ>
            <LogoMate>Mate</LogoMate>
          </Logo>
          <NavLinks>
            <NavHomeIcon onClick={scrollToHome} title="Home">
              <Icon component={AiFillHome} />
            </NavHomeIcon>
            <NavLink onClick={scrollToFeatures}>FEATURES</NavLink>
            <NavLink onClick={scrollToFAQ}>FAQ</NavLink>
            <NavLink onClick={scrollToPricing}>PRICING</NavLink>
          </NavLinks>
          <NavButtons>
            {user ? (
              <DashboardButton onClick={handleDashboard}>
                DASHBOARD
                <DashboardBadge>
                  {user.firstName?.charAt(0).toUpperCase() || 'U'}
                </DashboardBadge>
              </DashboardButton>
            ) : (
              <>
                <SignInButton onClick={handleSignIn}>SIGN IN</SignInButton>
                <PrimaryButton onClick={handleSignUp}>START FOR FREE</PrimaryButton>
              </>
            )}
          </NavButtons>
        </HeaderContainer>
      </WelcomeHeader>
      <Container>
        <Title>Terms of Service</Title>
        <LastUpdated>Last Updated: 26.02.2026</LastUpdated>

      <Section>
        <Paragraph>
          These Terms of Service ("Terms") govern your access to and use of sjMate ("Service"), a ski jumping performance 
          tracking platform operated by z-epic, Aljaž Žepič, s.p. ("we," "our," or "us"). By accessing or using our Service, you agree 
          to be bound by these Terms. If you disagree with any part of these Terms, you may not access or use the Service.
        </Paragraph>
        <Paragraph>
          These Terms constitute a legally binding agreement between you and us. Please read them carefully before using our Service.
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>1. Acceptance of Terms</SectionTitle>
        <Paragraph>
          By creating an account, accessing, or using sjMate, you acknowledge that you have read, understood, and agree to be 
          bound by these Terms and our Privacy Policy. If you are using the Service on behalf of an organization, you represent 
          that you have authority to bind that organization to these Terms.
        </Paragraph>
        <Paragraph>
          You must be at least 16 years old to use our Service. By creating an account, you confirm that you are 16 years of age or older.
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>2. Description of Service</SectionTitle>
        <Paragraph>
          sjMate is a cloud-based platform designed for tracking, analyzing, and managing ski jumping performance data. 
          Our Service includes:
        </Paragraph>
        <List>
          <ListItem>Training session recording and management</ListItem>
          <ListItem>Competition result tracking</ListItem>
          <ListItem>Performance analytics and reporting</ListItem>
          <ListItem>Athlete and equipment management</ListItem>
          <ListItem>Data import and export capabilities</ListItem>
          <ListItem>AI-powered coaching assistance (available with HS240 plan)</ListItem>
         
        </List>
        <Paragraph>
          We reserve the right to modify, suspend, or discontinue any aspect of the Service at any time, with or without notice.
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>3. User Accounts</SectionTitle>
        
        <SubSectionTitle>3.1 Account Creation</SubSectionTitle>
        <Paragraph>
          To use certain features of the Service, you must create an account. You agree to:
        </Paragraph>
        <List>
          <ListItem>Provide accurate, current, and complete information during registration</ListItem>
          <ListItem>Maintain and promptly update your account information</ListItem>
          <ListItem>Maintain the security of your account credentials</ListItem>
          <ListItem>Accept responsibility for all activities that occur under your account</ListItem>
          <ListItem>Notify us immediately of any unauthorized access or security breach</ListItem>
        </List>

        <SubSectionTitle>3.2 Account Types</SubSectionTitle>
        <Paragraph>
          We offer different account types (Coach, Athlete/Jumper) with varying features and access levels. Your account type 
          determines the features available to you.
        </Paragraph>

        <SubSectionTitle>3.3 Account Termination</SubSectionTitle>
        <Paragraph>
          You may terminate your account at any time through your account settings. We reserve the right to suspend or 
          terminate accounts that violate these Terms or engage in fraudulent, abusive, or illegal activity.
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>4. Subscription Plans and Payments</SectionTitle>
        
        <SubSectionTitle>4.1 Subscription Plans</SubSectionTitle>
        <Paragraph>
          We offer various subscription plans (FREE, HS60, HS90, HS130, HS240) with different features and limitations. 
          Subscription fees, features, and limitations are subject to change with notice.
        </Paragraph>

        <SubSectionTitle>4.2 Payment Terms</SubSectionTitle>
        <Paragraph>
          By subscribing to a paid plan, you agree to:
        </Paragraph>
        <List>
          <ListItem>Pay all fees associated with your subscription plan</ListItem>
          <ListItem>Provide accurate billing information</ListItem>
          <ListItem>Authorize us to charge your payment method for recurring subscription fees</ListItem>
          <ListItem>Pay any applicable taxes</ListItem>
        </List>

        <SubSectionTitle>4.3 Billing and Renewal</SubSectionTitle>
        <Paragraph>
          Subscriptions automatically renew at the end of each billing period unless cancelled. You may cancel your 
          subscription at any time through your account settings. Cancellation takes effect at the end of the current 
          billing period.
        </Paragraph>

        <SubSectionTitle>4.4 Refunds</SubSectionTitle>
        <Paragraph>
          Refund policies are determined on a case-by-case basis. Generally, we do not provide refunds for partial 
          subscription periods, except as required by law or at our sole discretion.
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>5. User Content and Data</SectionTitle>
        
        <SubSectionTitle>5.1 Your Content</SubSectionTitle>
        <Paragraph>
          You retain ownership of all data, information, and content you upload or enter into the Service ("User Content"). 
          By using the Service, you grant us a limited, non-exclusive, worldwide, royalty-free license to:
        </Paragraph>
        <List>
          <ListItem>Store, process, and display your User Content as necessary to provide the Service</ListItem>
          <ListItem>Generate analytics, reports, and insights from your User Content</ListItem>
          <ListItem>Create aggregated, anonymized data for service improvement purposes</ListItem>
        </List>

        <SubSectionTitle>5.2 Content Responsibility</SubSectionTitle>
        <Paragraph>
          You are solely responsible for your User Content. You represent and warrant that:
        </Paragraph>
        <List>
          <ListItem>You have the right to upload and use the User Content</ListItem>
          <ListItem>Your User Content does not violate any laws, regulations, or third-party rights</ListItem>
          <ListItem>Your User Content does not contain malicious code, viruses, or harmful content</ListItem>
          <ListItem>You have obtained necessary consents for any personal data of others included in your User Content</ListItem>
        </List>

        <SubSectionTitle>5.3 Data Backup</SubSectionTitle>
        <Paragraph>
          While we maintain regular backups, you are responsible for maintaining your own backups of important data. 
          We are not liable for any loss of User Content.
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>6. Acceptable Use</SectionTitle>
        <Paragraph>
          You agree not to:
        </Paragraph>
        <List>
          <ListItem>Use the Service for any illegal purpose or in violation of any laws</ListItem>
          <ListItem>Violate or infringe upon the rights of others, including intellectual property rights</ListItem>
          <ListItem>Upload malicious code, viruses, or harmful content</ListItem>
          <ListItem>Attempt to gain unauthorized access to the Service or other users' accounts</ListItem>
          <ListItem>Interfere with or disrupt the Service or servers connected to the Service</ListItem>
          <ListItem>Use automated systems to access the Service without authorization</ListItem>
          <ListItem>Reverse engineer, decompile, or disassemble any part of the Service</ListItem>
          <ListItem>Resell, sublicense, or redistribute the Service without our written permission</ListItem>
          <ListItem>Use the Service to transmit spam, unsolicited messages, or advertising</ListItem>
          <ListItem>Impersonate any person or entity or misrepresent your affiliation</ListItem>
        </List>
      </Section>

      <Section>
        <SectionTitle>7. Intellectual Property</SectionTitle>
        
        <SubSectionTitle>7.1 Our Intellectual Property</SubSectionTitle>
        <Paragraph>
          The Service, including its design, features, functionality, and content (excluding User Content), is owned by us 
          and protected by copyright, trademark, and other intellectual property laws. You may not copy, modify, distribute, 
          or create derivative works based on our Service without our express written permission.
        </Paragraph>

        <SubSectionTitle>7.2 Your Intellectual Property</SubSectionTitle>
        <Paragraph>
          You retain all rights to your User Content. We do not claim ownership of your User Content.
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>8. Privacy and Data Protection</SectionTitle>
        <Paragraph>
          Your privacy is important to us. Our collection, use, and protection of your personal information is governed by 
          our Privacy Policy, which is incorporated into these Terms by reference. By using the Service, you consent to 
          our data practices as described in the Privacy Policy.
        </Paragraph>
        <Paragraph>
          We comply with applicable data protection laws, including GDPR, and implement appropriate security measures to 
          protect your data.
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>9. Disclaimers and Limitations of Liability</SectionTitle>
        
        <SubSectionTitle>9.1 Service Availability</SubSectionTitle>
        <Paragraph>
          We strive to provide reliable service but do not guarantee that the Service will be uninterrupted, error-free, 
          or available at all times. The Service is provided "as is" and "as available" without warranties of any kind.
        </Paragraph>

        <SubSectionTitle>9.2 Disclaimer of Warranties</SubSectionTitle>
        <Paragraph>
          TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE DISCLAIM ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED 
          TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
        </Paragraph>

        <SubSectionTitle>9.3 Limitation of Liability</SubSectionTitle>
        <Paragraph>
          TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, 
          OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF PROFITS, DATA, OR USE, ARISING OUT OF OR RELATED TO YOUR 
          USE OF THE SERVICE.
        </Paragraph>
        <Paragraph>
          Our total liability for any claims arising from or related to the Service shall not exceed the amount you paid us 
          in the twelve (12) months preceding the claim.
        </Paragraph>

        <SubSectionTitle>9.4 No Professional Advice</SubSectionTitle>
        <Paragraph>
          The Service provides tools and information for tracking and analyzing ski jumping performance. It does not 
          constitute professional coaching, medical, or legal advice. You should consult with qualified professionals for 
          such advice.
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>10. Indemnification</SectionTitle>
        <Paragraph>
          You agree to indemnify, defend, and hold harmless us, our affiliates, officers, directors, employees, and agents 
          from and against any claims, liabilities, damages, losses, costs, or expenses (including reasonable attorneys' fees) 
          arising out of or related to:
        </Paragraph>
        <List>
          <ListItem>Your use of the Service</ListItem>
          <ListItem>Your User Content</ListItem>
          <ListItem>Your violation of these Terms</ListItem>
          <ListItem>Your violation of any rights of another party</ListItem>
        </List>
      </Section>

      <Section>
        <SectionTitle>11. Termination</SectionTitle>
        
        <SubSectionTitle>11.1 Termination by You</SubSectionTitle>
        <Paragraph>
          You may terminate your account at any time by deleting your account through your account settings or contacting us.
        </Paragraph>

        <SubSectionTitle>11.2 Termination by Us</SubSectionTitle>
        <Paragraph>
          We may suspend or terminate your access to the Service immediately, without prior notice, if you:
        </Paragraph>
        <List>
          <ListItem>Violate these Terms</ListItem>
          <ListItem>Engage in fraudulent, abusive, or illegal activity</ListItem>
          <ListItem>Fail to pay subscription fees when due</ListItem>
          <ListItem>Use the Service in a manner that could harm us or other users</ListItem>
        </List>

        <SubSectionTitle>11.3 Effect of Termination</SubSectionTitle>
        <Paragraph>
          Upon termination, your right to use the Service immediately ceases. We may delete your account and User Content 
          after a reasonable retention period, subject to legal and regulatory requirements.
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>12. Third-Party Services</SectionTitle>
        <Paragraph>
          The Service may integrate with third-party services, such as Google Drive. Your use of third-party services is 
          subject to their respective terms of service and privacy policies. We are not responsible for third-party services 
          or their terms, policies, or practices.
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>13. Modifications to Terms</SectionTitle>
        <Paragraph>
          We reserve the right to modify these Terms at any time. We will notify you of material changes by:
        </Paragraph>
        <List>
          <ListItem>Posting the updated Terms on our website</ListItem>
          <ListItem>Sending an email notification to your registered email address</ListItem>
          <ListItem>Displaying a notice within the Service</ListItem>
        </List>
        <Paragraph>
          Your continued use of the Service after changes become effective constitutes acceptance of the updated Terms. 
          If you do not agree to the changes, you must stop using the Service and terminate your account.
        </Paragraph>
      </Section>


      <Section>
        <SectionTitle>14. General Provisions</SectionTitle>
        
        <SubSectionTitle>14.1 Entire Agreement</SubSectionTitle>
        <Paragraph>
          These Terms, together with our Privacy Policy, constitute the entire agreement between you and us regarding the Service.
        </Paragraph>

        <SubSectionTitle>14.2 Severability</SubSectionTitle>
        <Paragraph>
          If any provision of these Terms is found to be unenforceable, the remaining provisions shall remain in full force and effect.
        </Paragraph>

        <SubSectionTitle>14.3 Waiver</SubSectionTitle>
        <Paragraph>
          Our failure to enforce any provision of these Terms shall not constitute a waiver of that provision or any other provision.
        </Paragraph>

        <SubSectionTitle>14.4 Assignment</SubSectionTitle>
        <Paragraph>
          You may not assign or transfer these Terms or your account without our prior written consent. We may assign these 
          Terms in connection with a merger, acquisition, or sale of assets.
        </Paragraph>

        <SubSectionTitle>14.5 Force Majeure</SubSectionTitle>
        <Paragraph>
          We shall not be liable for any failure or delay in performance due to circumstances beyond our reasonable control, 
          including natural disasters, war, terrorism, labor disputes, or internet failures.
        </Paragraph>
      </Section>

      <ContactInfo>
        <SectionTitle>Contact Us</SectionTitle>
        <Paragraph>
          If you have questions about these Terms, please contact us:
        </Paragraph>
        <Paragraph>
          <strong>Email:</strong> skijumpingmate@gmail.com
        </Paragraph>
        
      </ContactInfo>
      </Container>
      <Footer>
        <FooterContent>
          <FooterSection>
            <FooterLogo>
              <FooterLogoSJ>sj</FooterLogoSJ>
              <FooterLogoMate>Mate</FooterLogoMate>
            </FooterLogo>
            <div style={{ marginTop: '2.5rem' }}>
              <FooterTitle>About sjMate</FooterTitle>
              <FooterLink 
                href="/#features" 
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/#features');
                }}
                style={{ cursor: 'pointer' }}
              >
                Features
              </FooterLink>
              <FooterLink 
                href="/pricing" 
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/pricing');
                }}
                style={{ cursor: 'pointer' }}
              >
                Pricing
              </FooterLink>
              <FooterLink 
                href="/how-to-start" 
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/how-to-start');
                }}
                style={{ cursor: 'pointer' }}
              >
                How It Works
              </FooterLink>
            </div>
          </FooterSection>
          <FooterSection>
            <div style={{ marginTop: '2.5rem' }}>
              <FooterTitle>Legal</FooterTitle>
              <FooterLink href="/privacy-policy">Privacy Policy</FooterLink>
              <FooterLink href="/terms-of-service">Terms of Service</FooterLink>
            </div>
          </FooterSection>
          <FooterSection>
            <div style={{ marginTop: '2.5rem' }}>
              <FooterTitle>Contact</FooterTitle>
              <FooterLink href="mailto:skijumpingmate@gmail.com">skijumpingmate@gmail.com</FooterLink>
              <FooterLink href="https://instagram.com/sjmate" target="_blank" rel="noopener noreferrer">
                <Icon component={FiInstagram} style={{ display: 'inline-block', marginRight: '0.5rem', verticalAlign: 'middle' }} />
                Instagram
              </FooterLink>
            </div>
          </FooterSection>
        </FooterContent>
        <FooterBottom>
          <FooterText>
            Created and designed in 2026 by <b>z-epic</b>
          </FooterText>
        </FooterBottom>
        <FooterLine />
      </Footer>
    </>
  );
};

export default TermsOfService;

